import React, { FC, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  ActionsGroup,
  DialogActions,
  DialogContent,
  DialogTitle,
  FlatButton,
  Flex,
  IconButton,
  RaisedButton,
  Slider,
} from "@evergis/ui";

import { ModalDialog } from "../DialogComponents/ModalDialog";
import { DialogDescription, SliderContainer } from "./styled";
import { useConfirmDialog, useDialogReset } from "../../../hooks/useDialogs";
import { useCurrentLayer } from "../../../hooks/layer/useCurrentLayer";
import { getScaleSegments, getSelectedSegment } from "../../../ducks/style/selectors";
import { removeSymbolClassification, setScaleSegments, setSelectedSegment } from "../../../ducks/style/style";
import { useCreateSegments } from "../../../modules/Style/hooks/useCreateSegments";
import { useDefaultScaleRange } from "../../../hooks/style/useDefaultScaleRange";
import {
  resetClassification,
  resetClassificationHistory,
  setClassificationSegment,
} from "../../../ducks/classification/classification";

export const ScaleSegmentation: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("dialogs");
  const closeDialog = useDialogReset();
  const layer = useCurrentLayer();
  const selectedSegment = useSelector(getSelectedSegment);
  const scaleSegments = useSelector(getScaleSegments);
  const [segments, setSegments] = useState(scaleSegments);
  const showConfirmDialog = useConfirmDialog();
  const createSegments = useCreateSegments();
  const defaultScaleRange = useDefaultScaleRange();

  const onSave = useCallback(() => {
    if (!segments.includes(defaultScaleRange[0])) {
      segments.push(defaultScaleRange[0]);
    }
    if (!segments.includes(defaultScaleRange[1])) {
      segments.push(defaultScaleRange[1]);
    }

    setScaleSegments(segments);
    setSelectedSegment(segments[0]);
    createSegments(segments, true);
    closeDialog();
  }, [closeDialog, createSegments, defaultScaleRange, segments]);

  const onResetConfirm = useCallback(() => {
    if (selectedSegment !== undefined) {
      dispatch(setClassificationSegment({ [selectedSegment]: null }));
    }

    setSegments(defaultScaleRange);
    dispatch(setScaleSegments(defaultScaleRange));
    dispatch(setSelectedSegment(undefined));

    dispatch(removeSymbolClassification());
    dispatch(resetClassification());
    dispatch(resetClassificationHistory());
  }, [defaultScaleRange, dispatch, selectedSegment]);

  const onReset = useCallback(() => {
    showConfirmDialog([""], {
      title: t("scaleSegment.resetTitle"),
      content: t("scaleSegment.resetText"),
      onConfirm: onResetConfirm,
      okText: t("actions.confirm", { ns: "common" }),
      cancelText: t("actions.cancel", { ns: "common" }),
      options: {
        maxWidth: "40rem",
        closable: true,
      },
    });
  }, [onResetConfirm, showConfirmDialog, t]);

  return (
    <ModalDialog maxWidth="40rem" minHeight="21.25rem">
      <DialogTitle style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <Flex style={{ width: "100%" }}>
          <ActionsGroup>{t("scaleSegmentation", { ns: "style" })}</ActionsGroup>
          <ActionsGroup>
            <IconButton kind="close" onClick={closeDialog} />
          </ActionsGroup>
        </Flex>
        <DialogDescription>{t("scaleSegment.description")}</DialogDescription>
      </DialogTitle>
      <DialogContent>
        <SliderContainer>
          <Slider
            min={defaultScaleRange[0]}
            max={defaultScaleRange[1]}
            value={[layer.minResolution, layer.maxResolution]}
            marks={segments}
            trackTooltip={`${t("scaleSegment.scaleRestrict")}: ${layer.minResolution} - ${layer.maxResolution}`}
            sliderWidth="100%"
            markLabels
            isDragMarks
            range
            onChangeMarks={setSegments}
          />
        </SliderContainer>
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <RaisedButton primary onClick={onSave}>
            {t("actions.save", { ns: "common" })}
          </RaisedButton>
          <FlatButton onClick={closeDialog}>{t("actions.cancel", { ns: "common" })}</FlatButton>
          <FlatButton onClick={onReset}>{t("scaleSegment.reset")}</FlatButton>
        </ActionsGroup>
      </DialogActions>
    </ModalDialog>
  );
};
